import React, { useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { SecondaryButton } from '../../styles/sharedComponents';
import {
  UserMenuContainer,
  UserMenuDropdown,
  UserInfo,
  LogoutButton,
} from './styles';

interface UserMenuProps {
  user: { displayName?: string; email?: string } | null;
  isLoggingOut: boolean;
  onLogout: () => void;
}

export const UserMenu: React.FC<UserMenuProps> = ({ user, isLoggingOut, onLogout }) => {
  const { t } = useTranslation();
  const [menuOpen, setMenuOpen] = React.useState(false);
  const [isClosing, setIsClosing] = React.useState(false);
  const [buttonWidth, setButtonWidth] = React.useState(0);

  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Função para fechar menu com animação
  const closeMenu = () => {
    setIsClosing(true);
    setTimeout(() => {
      setMenuOpen(false);
      setIsClosing(false);
    }, 150); // Duração da animação
  };

  // Função para abrir menu
  const openMenu = () => {
    if (buttonRef.current) {
      setButtonWidth(buttonRef.current.offsetWidth);
    }
    setMenuOpen(true);
    setIsClosing(false);
  };

  // Clique fora para fechar
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuOpen &&
        menuRef.current &&
        buttonRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        closeMenu();
      }
    };

    if (menuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [menuOpen]);

  const handleLogout = () => {
    closeMenu();
    onLogout();
  };

  if (!user) return null;

  return (
    <UserMenuContainer>
      <SecondaryButton
        ref={buttonRef}
        onClick={() => menuOpen ? closeMenu() : openMenu()}
      >
        <div style={{
          width: 16,
          height: 16,
          borderRadius: '50%',
          background: '#ff4444',
          marginRight: 8,
          flexShrink: 0
        }} />
        {user.displayName?.split(' ')[0] || 'Usuário'}
      </SecondaryButton>

      {(menuOpen || isClosing) && (
        <UserMenuDropdown
          ref={menuRef}
          $isOpen={menuOpen}
          $isClosing={isClosing}
          $buttonWidth={buttonWidth}
        >
          <UserInfo>
            {user.displayName || user.email}
          </UserInfo>
          <LogoutButton onClick={handleLogout} disabled={isLoggingOut}>
            {isLoggingOut ? (
              <div style={{ width: '14px', height: '14px', border: '2px solid #666', borderTop: '2px solid #fff', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
            ) : (
              <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
            )}
            {isLoggingOut ? t('nav.loggingOut') : t('nav.logout')}
          </LogoutButton>
        </UserMenuDropdown>
      )}
    </UserMenuContainer>
  );
};
